/**
 * @file ChannelCardDrawer.tsx
 * @description Drawer «Карточка канала» (phase17.1, шаги 9-11).
 *
 * Эффективная карточка канала (COALESCE оверлей→база): overridable-поля с
 * бейджами наследуется/переопределено и «↩ к базовому», neverOverride read-only
 * (🔒), секция MARKETPLACE, галерея, предупреждение о ценовом паритете (C5).
 * Mobile-first: fullScreen на < sm. Сохранение — PUT upsert (пустое поле =
 * наследовать). Публикация — тумблер в шапке.
 */

import { AlertTriangle, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../../components/ui/sheet";
import { Switch } from "../../../../components/ui/switch";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ApiError } from "../../../../lib/api-client";
import {
  CHANNEL_LABELS,
  channelListings,
  type ChannelCard,
  type ManualBadgeKind,
  type SalesChannelKind,
} from "../../../../lib/api-channel-listings";
import { ChannelBadgeSection, toIso, toLocalInput } from "./ChannelBadgeSection";
import {
  GalleryInfo,
  LegalSection,
  MarketplaceSection,
  OverridableTextField,
} from "./ChannelCardFields";

interface Props {
  productId: string;
  channel: string;
  onClose: () => void;
  onSaved: () => void;
}

/** Локальная форма overridable-полей (пусто = наследовать). */
interface FormState {
  displayName: string;
  shortDescription: string;
  description: string;
  priceOverride: string;
  minPrice: string;
  isPublished: boolean;
  /** phase18.3 (P4) — ручной бейдж витрины ("" = без бейджа). */
  manualBadge: "" | ManualBadgeKind;
  /** Значение <input type="datetime-local">. */
  badgeUntil: string;
}

/** Значение поля для input: показываем override, иначе пусто (наследование). */
function fieldInput(f: { value: string | null; origin: string }): string {
  return f.origin === "overridden" && f.value !== null ? f.value : "";
}

export function ChannelCardDrawer({ productId, channel, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [card, setCard] = useState<ChannelCard | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hydrate = useCallback((c: ChannelCard) => {
    setCard(c);
    setForm({
      displayName: fieldInput(c.displayName),
      shortDescription: fieldInput(c.shortDescription),
      description: fieldInput(c.description),
      priceOverride: c.serializedPrice.origin === "overridden" && c.serializedPrice.value !== null
        ? String(c.serializedPrice.value)
        : "",
      minPrice: c.serializedPrice.minPrice !== null ? String(c.serializedPrice.minPrice) : "",
      isPublished: c.isPublished,
      manualBadge: c.manualBadge ?? "",
      badgeUntil: toLocalInput(c.badgeUntil),
    });
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    channelListings
      .card(productId, channel)
      .then((c) => alive && hydrate(c))
      .catch((err) => alive && setError(err instanceof ApiError ? err.message : "Не удалось загрузить карточку"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [productId, channel, hydrate]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function onSave() {
    if (!form) return;
    setSaving(true);
    try {
      const parseNum = (s: string): number | null => (s.trim() === "" ? null : Number.parseFloat(s));
      const priceNum = parseNum(form.priceOverride);
      if (form.priceOverride.trim() !== "" && Number.isNaN(priceNum)) {
        notify.error("Неверная цена");
        return;
      }
      const updated = await channelListings.upsert(productId, channel, {
        isPublished: form.isPublished,
        displayName: form.displayName.trim() || null,
        shortDescription: form.shortDescription.trim() || null,
        description: form.description.trim() || null,
        priceOverride: priceNum,
        minPrice: parseNum(form.minPrice),
        manualBadge: form.manualBadge || null,
        badgeUntil: toIso(form.badgeUntil),
      });
      hydrate(updated);
      notify.success("Карточка канала сохранена");
      onSaved();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  const label = CHANNEL_LABELS[channel as SalesChannelKind] ?? channel;
  const isMarketplace = card?.channelType === "MARKETPLACE";
  const basePrice = card?.serializedPrice.basePrice ?? null;
  const priceNum = form ? Number.parseFloat(form.priceOverride) : NaN;
  const priceParityWarn =
    isMarketplace && basePrice !== null && !Number.isNaN(priceNum) && priceNum < basePrice;

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full max-w-full sm:w-3/4 sm:max-w-xl overflow-y-auto p-0">
        <SheetHeader className="p-5 pb-3 border-b border-outline-variant">
          <SheetTitle>Карточка: {label}</SheetTitle>
          {card ? (
            <div className="flex items-center gap-3 mt-1">
              <Label className="text-sm flex items-center gap-2">
                <Switch checked={form?.isPublished ?? false} onCheckedChange={(v) => set("isPublished", v)} />
                Публиковать
              </Label>
              {isMarketplace && card.needsResync ? (
                <span className="text-xs text-on-surface-variant">· требует ре-синк</span>
              ) : null}
            </div>
          ) : null}
        </SheetHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="size-5 animate-spin text-on-surface-variant" />
          </div>
        ) : error || !card || !form ? (
          <div className="p-5 text-sm text-destructive">{error ?? "Нет данных"}</div>
        ) : (
          <div className="p-5 flex flex-col gap-5">
            {/* Контент */}
            <section className="flex flex-col gap-3">
              <h4 className="type-caption font-medium text-on-surface-variant">Контент</h4>
              <OverridableTextField
                id="ch-name" label="Название" baseHint={card.displayName.value}
                value={form.displayName} onChange={(v) => set("displayName", v)} onReset={() => set("displayName", "")}
              />
              <OverridableTextField
                id="ch-short" label="Короткое описание" baseHint={card.shortDescription.value}
                value={form.shortDescription} onChange={(v) => set("shortDescription", v)} onReset={() => set("shortDescription", "")}
              />
              <OverridableTextField
                id="ch-desc" label="Маркетинговое описание" baseHint={card.description.value} multiline
                value={form.description} onChange={(v) => set("description", v)} onReset={() => set("description", "")}
              />
            </section>

            {/* Цена (C5) */}
            <section className="flex flex-col gap-2">
              <h4 className="type-caption font-medium text-on-surface-variant">Цена</h4>
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="ch-price" className="text-xs">Цена в канале (₽)</Label>
                  <Input id="ch-price" type="number" min="0" step="0.01" className="w-36"
                    value={form.priceOverride} placeholder={basePrice !== null ? String(basePrice) : "как у товара"}
                    onChange={(e) => set("priceOverride", e.target.value)} />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="ch-minprice" className="text-xs">Мин. цена / пол (₽)</Label>
                  <Input id="ch-minprice" type="number" min="0" step="0.01" className="w-36"
                    value={form.minPrice} placeholder="—" onChange={(e) => set("minPrice", e.target.value)} />
                </div>
                {basePrice !== null ? (
                  <span className="text-xs text-on-surface-variant pb-2">базовая: {basePrice} ₽</span>
                ) : null}
              </div>
              {priceParityWarn ? (
                <p className="text-xs text-warning inline-flex items-center gap-1">
                  <AlertTriangle className="size-3.5" />
                  Ниже базовой ({basePrice} ₽) — маркетплейс может понизить товар в индексе цен.
                </p>
              ) : null}
            </section>

            <ChannelBadgeSection
              manualBadge={form.manualBadge}
              badgeUntil={form.badgeUntil}
              onBadgeChange={(v) => set("manualBadge", v)}
              onUntilChange={(v) => set("badgeUntil", v)}
            />

            <LegalSection legal={card.legal} />
            <GalleryInfo media={card.media} />
            {card.marketplace ? <MarketplaceSection mp={card.marketplace} /> : null}
          </div>
        )}

        <div className="sticky bottom-0 bg-surface-lowest border-t border-outline-variant p-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Закрыть</Button>
          <Button onClick={onSave} disabled={saving || loading || !form}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : "Сохранить"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
